import { useRef, useState } from "react";
import { UploadCloud, X, Loader2 } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";

/** Resolve a stored image path (either a full URL or /api/files/... path) to a browser-loadable src. */
export const resolveImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

export default function ImageUploader({ value, onChange, testId = "image-uploader", endpoint = "/admin/upload" }) {
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  const clear = () => onChange("");

  const src = resolveImageUrl(value);

  return (
    <div className="space-y-2" data-testid={testId}>
      {src ? (
        <div className="relative group rounded-2xl overflow-hidden border border-stone-200">
          <img src={src} alt="Uploaded" className="w-full h-40 object-cover" />
          <button
            type="button"
            onClick={clear}
            data-testid={`${testId}-remove`}
            className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/95 grid place-items-center shadow-md hover:bg-white text-stone-700"
            aria-label="Remove image"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <label
          onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`flex flex-col items-center justify-center gap-2 h-40 rounded-2xl border-2 border-dashed cursor-pointer transition-colors ${dragging ? "border-blue-500 bg-sky-50" : "border-stone-200 bg-stone-50 hover:border-amber-300 hover:bg-amber-50/50"}`}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid={`${testId}-input`}
            onChange={(e) => upload(e.target.files?.[0])}
          />
          {uploading ? (
            <>
              <Loader2 className="h-6 w-6 text-blue-900 animate-spin" />
              <p className="text-sm text-stone-500">Uploading…</p>
            </>
          ) : (
            <>
              <UploadCloud className="h-6 w-6 text-stone-400" />
              <p className="text-sm text-stone-600">Drop an image here, or <span className="text-blue-800 font-medium">click to browse</span></p>
              <p className="text-xs text-stone-400">PNG, JPG, WEBP or GIF · up to 8 MB</p>
            </>
          )}
        </label>
      )}
    </div>
  );
}
